/**
 * OCTOPUS — ClassifierSettings (set-points do classificador de etapas)
 *
 * Seção "Classificador de etapas (Operações)" do cadastro do equipamento.
 * Exposta SÓ quando o equipamento tem equipment_classifier_config (dragas
 * monitoradas pela máquina de atividade). Set-points POR EQUIPAMENTO
 * (diretiva do dono: nunca hardcoded; 0 desliga o mecanismo):
 *  - stage_exit_hysteresis_min  (RC-2, default 30): saída de Carregando/
 *    Descarregando segura a janela; dip absorvido conta tempo+combustível.
 *  - stage_enter_hysteresis_min (dono 2026-07-04, default 5): entrada em
 *    Carregando/Descarregando exige persistência (blip de limpeza de cano não
 *    vira etapa nem contamina o estado de carga); commit retroage ao início.
 *  - motion_hysteresis_min      (dono 2026-07-04, default 10): transições
 *    Parado↔Deslocamento só viram etapa se persistirem a janela (mata o
 *    micro-picote de manobra); o registro retroage ao instante da virada.
 *  - base_close_shutdown_min    (dono 2026-07-04, default 60): viagem ancorada
 *    fecha na ENTRADA da base quando a propulsão desliga dentro da janela.
 *  - idle_offwork_min_min       (R1, dono 2026-07-07, default 240): parada mais
 *    longa que a janela tem o tempo de motor DESLIGADO excluído do relógio
 *    produtivo do índice de tempo. Aplicado NA LEITURA (sem reprocessar).
 *  - idle_rule_mode             (R1): regra da parada fora de operação
 *    (híbrido/duração/motor). "Motor ligado ⟺ RPM>1" é física — não é campo.
 *
 * GET/PUT /api/lab/classifier-config/{id} — o worker relê a config em ~60s.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, TimerReset } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { showErrorToast } from '@/utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// key no thresholds, rótulo, default e descrição curta (0 sempre desliga).
// DOIS grupos com contratos operacionais DIFERENTES (auditoria de design
// 2026-07-07): STAGE_FIELDS mudam a máquina de estados no write-path (o worker
// regrava etapas); INDEX_FIELDS mudam SÓ o cálculo on-read do índice de tempo
// (efeito imediato, sem regravar nada) — por isso o subgrupo visual separado.
const STAGE_FIELDS = [
  {
    key: 'stage_exit_hysteresis_min',
    label: 'Histerese de saída Carregando/Descarregando (min)',
    def: 30,
    hint: 'Interrupções mais curtas não fecham a etapa (tempo e combustível contam); sem retomada, fecha retroativo ao instante da interrupção.',
  },
  {
    key: 'stage_enter_hysteresis_min',
    label: 'Histerese de entrada Carregando/Descarregando (min)',
    def: 5,
    hint: 'Bomba trabalhando por menos que a janela (ex.: limpeza de canos) não vira etapa nem muda o estado de carga; trabalho confirmado retroage ao início real.',
  },
  {
    key: 'motion_hysteresis_min',
    label: 'Histerese Parado ↔ Deslocamento (min)',
    def: 10,
    hint: 'Alternâncias mais curtas que a janela (manobra/jitter de velocidade) não viram etapa; a transição confirmada retroage ao instante real da virada.',
  },
  {
    key: 'base_close_shutdown_min',
    label: 'Fechamento na base: janela de desligamento da propulsão (min)',
    def: 60,
    hint: 'Viagem ancorada fecha na ENTRADA da zona-base quando os motores de propulsão desligam dentro desta janela; sem desligamento, a nova carga fecha.',
  },
];

const INDEX_FIELDS = [
  {
    key: 'idle_offwork_min_min',
    label: 'Parada fora de operação: duração mínima (min)',
    def: 240,
    hint: 'Parada mais longa que esta janela tem o tempo de motor DESLIGADO excluído do relógio produtivo (tripulação fora — pernoite/fim de semana); o tempo de motor ligado permanece como espera operacional.',
  },
];

const FIELDS = [...STAGE_FIELDS, ...INDEX_FIELDS];

// Regra da parada fora de operação (R1). "Motor ligado = RPM > 1" é física
// imutável — aparece como texto fixo, nunca como campo (diretiva do dono).
const IDLE_RULE_MODES = [
  { value: 'hibrido', label: 'Híbrido (duração + motor) — recomendado' },
  { value: 'duracao', label: 'Só duração (parada longa sai inteira)' },
  { value: 'motor', label: 'Só motor (desconta motor desligado sempre)' },
];

const ClassifierSettings = ({ equipmentId }) => {
  const [config, setConfig] = useState(null); // null = sem config → seção oculta
  const [values, setValues] = useState({});
  const [idleMode, setIdleMode] = useState('hibrido');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setConfig(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/lab/classifier-config/${equipmentId}`, { headers: getAuthHeaders() });
        if (!active) return;
        const data = res.data || {};
        if (!data.configured) return; // equipamento fora da máquina de atividade
        setConfig(data);
        const next = {};
        for (const f of FIELDS) {
          const cur = data.thresholds?.[f.key];
          next[f.key] = cur != null ? String(cur) : String(f.def);
        }
        setValues(next);
        setIdleMode(data.idle_rule_mode || 'hibrido');
      } catch (err) {
        // não-crítico: sem acesso/erro → seção não aparece
      }
    })();
    return () => { active = false; };
  }, [equipmentId]);

  const handleSave = useCallback(async () => {
    const parsed = {};
    for (const f of FIELDS) {
      const n = Number(values[f.key]);
      if (!Number.isFinite(n) || n < 0 || n > 1440) {
        toast.error(`"${f.label}" deve estar entre 0 e 1440 minutos (0 desliga).`);
        return;
      }
      parsed[f.key] = n;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/lab/classifier-config/${equipmentId}`,
        { thresholds: { ...(config?.thresholds || {}), ...parsed }, idle_rule_mode: idleMode },
        { headers: getAuthHeaders() },
      );
      setConfig((prev) => ({ ...(prev || {}), ...(res.data || {}) }));
      toast.success('Set-points salvos. O classificador aplica em ~1 min.');
    } catch (err) {
      showErrorToast(err, 'Erro ao salvar os set-points do classificador.');
    } finally {
      setSaving(false);
    }
  }, [equipmentId, values, idleMode, config]);

  if (!config) return null;

  const renderField = (f) => (
    <div key={f.key} className="space-y-1">
      <p className="text-sm">{f.label}</p>
      <p className="text-xs text-muted-foreground">{f.hint}</p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="0"
          max="1440"
          step="1"
          className="w-32"
          value={values[f.key] ?? ''}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          data-testid={`classifier-${f.key}-input`}
        />
        <span className="text-sm text-muted-foreground">min</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-3 border rounded-lg p-3" data-testid="classifier-settings">
      <Label className="flex items-center gap-2">
        <TimerReset className="w-4 h-4 text-muted-foreground" />
        Classificador de etapas (Operações)
      </Label>
      {STAGE_FIELDS.map(renderField)}

      {/* Subgrupo on-read (R1): mexe só no cálculo do índice, não regrava etapas. */}
      <div className="pt-3 mt-1 border-t space-y-1">
        <p className="text-sm font-medium">Índice de tempo produtivo</p>
        <p className="text-xs text-muted-foreground">
          Aplicado na leitura: muda o cálculo do índice na hora, sem regravar as etapas.
        </p>
      </div>
      {INDEX_FIELDS.map(renderField)}
      <div className="space-y-1">
        <p className="text-sm">Regra de parada fora de operação</p>
        <p className="text-xs text-muted-foreground">
          Como o índice de tempo trata paradas longas. Motor ligado = RPM &gt; 1
          (física do motor — não configurável).
        </p>
        <Select value={idleMode} onValueChange={setIdleMode}>
          <SelectTrigger className="w-full sm:w-96" data-testid="classifier-idle_rule_mode-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {IDLE_RULE_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={handleSave}
        disabled={saving}
        className="gap-1"
        data-testid="classifier-thresholds-save"
      >
        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
        Salvar set-points
      </Button>
    </div>
  );
};

export default ClassifierSettings;
